import { BaseButton } from '@/components/Buttons/base';
import { PrimaryButton } from '@/components/Buttons/Primary/primaryButton';
import { Product } from '@/context/types';
import { Link } from 'react-router-dom'; // Adjust based on your routing

export default function BoxListItem({
  product,
  selectedQty,
  onAdd,
  onRemove,
  onDetailClick,
  disabled,
}: {
  product: Product; // Replace with your actual type
  selectedQty: number;
  onAdd: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onRemove: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDetailClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-row items-center justify-between w-full py-2 px-4 odd:bg-white even:bg-brand-frost/70 transition-colors">
      {/* Left Area: Image & Typography */}
      <div className="flex items-center gap-4 flex-1 min-w-0 pr-4">
        {/* Circular Image Thumbnail */}
        <div className="w-[32px] h-[32px] shrink-0 flex items-center justify-center overflow-hidden rounded-full shadow-xs relative bg-white">
          {product?.img?.url ? (
            <img
              src={product.img.url}
              alt={product.img.altText ?? product.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-text-muted/50 font-display text-[0.6rem] tracking-widest uppercase text-center leading-tight px-1">
              {product.productType || 'Item'}
            </span>
          )}
          {/* Disabled Overlay */}
          {disabled && selectedQty === 0 && (
            <div className="absolute inset-0 bg-white/50 z-10" />
          )}
        </div>

        {/* Typography & Read More Link */}
        <div className="flex flex-col items-start w-full min-w-0">
          <span
            role="button"
            onClick={onDetailClick}
            className="text-text-heading !text-[1em] font-[500] tracking-wider leading-tight truncate w-full hover:underline cursor-pointer"
            title="View Details"
          >
            {product.title}
          </span>

          {/* Calories & Allergens Metadata Wrapper */}
          {(product?.nutritionFacts?.calories ||
            (product?.allergens && product.allergens.length > 0)) && (
            <div
              className="w-full mt-1 truncate leading-relaxed"
              title={
                product?.allergens?.length
                  ? `Contains: ${product.allergens.join(', ')}`
                  : undefined
              }
            >
              {/* Calorie Count */}
              {product?.nutritionFacts?.calories && (
                <span className="inline-flex items-center text-text-muted text-[10px] leading-[10px] font-[400] align-middle mr-1.5">
                  {/* Minimalist Stroked Flame Icon */}
                  <svg
                    className="w-[10px] h-[10px] mr-0.5 text-text-muted/80"
                    fill="none"
                    stroke="var(--color-brand-burgundy)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                  </svg>
                  {product?.nutritionFacts?.calories} cal
                </span>
              )}

              {/* Optional Dot Separator */}
              {product?.nutritionFacts?.calories &&
                product?.allergens?.length! > 0 && (
                  <span className="inline-block text-brand-truffle/20 text-[10px] align-middle mr-1.5">
                    &bull;
                  </span>
                )}

              {/* Allergen List (Inline Blocks for Native Ellipsis) */}
              {product?.allergens?.map((allergen: string) => (
                <span
                  key={allergen}
                  className="inline-block px-1.5 py-[1px] border border-brand-truffle/10 bg-brand-truffle/5 text-text-muted/80 text-[9px] rounded-sm font-[500] uppercase tracking-wider cursor-default select-none align-middle mr-1"
                >
                  {allergen}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Area: Controls Container */}
      <div className="h-[25px] flex items-center justify-end w-[120px] shrink-0">
        {selectedQty === 0 ? (
          <BaseButton
            onClick={onAdd}
            disabled={disabled}
            variant="none"
            className="bg-brand-truffle border-1 border-brand-truffle hover:border-brand-truffle-2 hover:bg-brand-cream focus:bg-brand-cream focus:text-brand-truffle hover:text-brand-truffle font-display text-brand-cream !font-[400] text-[0.6em] leading-[1em] w-full h-full !px-0 !py-0 !rounded-full flex items-center justify-center lift-light transition-colors disabled:border-gray-300"
          >
            Add to box
          </BaseButton>
        ) : (
          <div className="grid grid-cols-3 items-center bg-brand-cream border-1 border-brand-truffle rounded-full w-full h-full lift-light animate-in fade-in zoom-in duration-200 overflow-hidden">
            <button
              onClick={onRemove}
              className="w-[3em] h-[25px] p-0 flex items-center justify-center text-brand-truffle hover:bg-brand-truffle hover:text-white transition-colors focus:bg-brand-truffle focus:text-white"
            >
              <svg
                className="h-[0.9em] w-[0.9em]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={4}
                  d="M20 12H4"
                />
              </svg>
            </button>
            <span className="text-center text-brand-truffle !font-[700] text-[0.8em] leading-[1em]">
              {selectedQty}
            </span>
            <button
              onClick={onAdd}
              disabled={disabled}
              className="w-[3em] h-[25px] p-0 flex items-center justify-center text-brand-truffle hover:bg-brand-truffle hover:text-white transition-colors focus:bg-brand-truffle focus:text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              <svg
                className="h-[0.9em] w-[0.9em]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={4}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
